package pt.at.oss.kotlin.manual.validator.common

import pt.at.oss.kotlin.core.enumeration.Country
import pt.at.oss.kotlin.core.enumeration.PeculiarCountry
import pt.at.oss.kotlin.manual.enumeration.AddressType
import pt.at.oss.kotlin.manual.enumeration.CustomErrorCodes
import pt.at.oss.kotlin.manual.storage.common.Address
import pt.lightweightform.kotlin.type.validator.ValidationCodes
import pt.lightweightform.kotlin.type.validator.ValidationError

/**
 * Created by Pedro Alfaiate on 14/05/2020.
 */
class AddressValidator {
    companion object {
        val ADDRESS_COUNTRIES: Array<Country> = Country.values()
    }

    val validateAddressSequencial = fun(address: Address): List<ValidationError> {
        val errors = mutableListOf<ValidationError>()

        // Always shown
        // type
        validateAddressType(address.type ?: "<null>")
            ?.also { errors.add(it) }

        // country
        validateCountry(address.country ?: "<null>")
            ?.also { errors.add(it) }

        // region
        validateRegion(address.region, address.country)
            ?.also { errors.add(it) }

        // freeText
        validateRequiredStrIf(address.freeText, address.type == AddressType.FREE_TEXT.value)
            ?.also { errors.add(it) }

        FreeTextValidator.validateFreeText(address.freeText)
            ?.also { errors.add(it) }

        // street
        FreeTextValidator.validateFreeText(address.street)
            ?.also { errors.add(it) }

        // buildingNumber
        FreeTextValidator.validateFreeText(address.buildingNumber)
            ?.also { errors.add(it) }

        // floorNumber
        FreeTextValidator.validateFreeText(address.floorNumber)
            ?.also { errors.add(it) }

        // city
        FreeTextValidator.validateFreeText(address.city)
            ?.also { errors.add(it) }

        // suite
        FreeTextValidator.validateFreeText(address.suite)
            ?.also { errors.add(it) }

        // district
        FreeTextValidator.validateFreeText(address.district)
            ?.also { errors.add(it) }

        // postOfficeBox
        FreeTextValidator.validateFreeText(address.postOfficeBox)
            ?.also { errors.add(it) }

        // countrySubEntry
        FreeTextValidator.validateFreeText(address.countrySubEntity)
            ?.also { errors.add(it) }

        // otherLocalId
        FreeTextValidator.validateFreeText(address.otherLocalId)
            ?.also { errors.add(it) }

        // Structured address fields
        errors.addAll(validateStructuredFieldsSequencial(address, address.type == AddressType.STRUCTURED.value))

        return errors
    }

    val validateAddressType = fun(addressType: String): ValidationError? {
        // allowedValues
        return if (AddressType.values().none { it.value == addressType }) ValidationError(code = ValidationCodes.UNALLOWED_VALUE_CODE)
        else null
    }

    val validateCountry = fun(country: String): ValidationError? {
        // allowedValues
        return if (ADDRESS_COUNTRIES.none { it.codeA2 == country }) ValidationError(code = CustomErrorCodes.ADDRESS_COUNTRY_INVALID.code)
        else null
    }

    val validateRegion = fun(regionA2: String?, countryA2: String?): ValidationError? {
        val region = regionA2?.let { PeculiarCountry.findByA2(regionA2) }
        return if (regionA2 == null)
            null
        else if (region == null)
            ValidationError(code = ValidationCodes.UNALLOWED_VALUE_CODE)
        else if (countryA2 != null && region.parentA2 != countryA2)
            ValidationError(code = CustomErrorCodes.ADDRESS_REGION_WRONG_COUNTRY.code)
        else if (PeculiarCountry.ARTIFICIAL_CODE_ADDRESS.contains(region.codeA2))
            ValidationError(code = CustomErrorCodes.ADDRESS_WITH_ARTIFICIAL_CODE_NOT_ALLOWED.code)
        else
            null
    }

    val validateStructuredFieldsSequencial = fun(address: Address, isStructured: Boolean): List<ValidationError> {
        val errors = mutableListOf<ValidationError>()

        // structured in MOSS
        // street
        validateStructuredFieldProhibition(address.street, isStructured)?.also { errors.add(it) }

        // buildingNumber
        validateStructuredFieldProhibition(address.buildingNumber, isStructured)?.also { errors.add(it) }

        // floorNumber
        validateStructuredFieldProhibition(address.floorNumber, isStructured)?.also { errors.add(it) }

        // postCode
        val shouldEnforceFormat = formattedPostCodeRequired(isStructured, address.country)
        validateStructuredFieldProhibition(address.postCode, isStructured)?.also { errors.add(it) }
        validateRequiredStrIf(address.postCode, shouldEnforceFormat)?.also { errors.add(it) }
        validatePostCodeFormatIf(address.postCode, shouldEnforceFormat)?.also { errors.add(it) }
        FreeTextValidator.validateFreeText(address.postCode)?.also { errors.add(it) }

        // city
        validateRequiredStrIf(address.city, isStructured)?.also { errors.add(it) }
        validateStructuredFieldProhibition(address.city, isStructured)?.also { errors.add(it) }

        // structured extra
        // suite
        validateStructuredFieldProhibition(address.suite, isStructured)?.also { errors.add(it) }

        // postOfficeBox
        validateStructuredFieldProhibition(address.postOfficeBox, isStructured)?.also { errors.add(it) }

        // district
        validateStructuredFieldProhibition(address.district, isStructured)?.also { errors.add(it) }

        // countrySubEntity
        validateStructuredFieldProhibition(address.countrySubEntity, isStructured)?.also { errors.add(it) }

        // otherLocalId
        validateStructuredFieldProhibition(address.otherLocalId, isStructured)?.also { errors.add(it) }

        return errors
    }

    val validateStructuredFieldProhibition = fun(fieldValue: String, isStructured: Boolean): ValidationError? {
        return if (!isStructured && fieldValue.isNotBlank()) ValidationError(code = CustomErrorCodes.ADDRESS_STRUCTURED_FIELDS_BUT_NOT_STRUCTURED.code)
        else null
    }

    val validatePostCodeFormatIf = fun(postCode: String, shouldEnforceFormat: Boolean): ValidationError? {
        val postCodeFormat = Regex("^[0-9]{4}-[0-9]{3}$")
        return if (shouldEnforceFormat && !postCode.matches(postCodeFormat)) ValidationError(code = CustomErrorCodes.ADDRESS_POST_CODE_INVALID_FORMAT.code)
        else null
    }

    val formattedPostCodeRequired = fun (isStructured: Boolean, country: String?): Boolean {
        return isStructured && country == Country.PORTUGAL.codeA2
    }
}
